<script lang="ts">
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import { solfegeMemeTemplates } from './_solfegeMemeTemplates';
  import type { SolfegeMemeTemplate } from './_solfegeMemeTemplates';
  import { onMount } from 'svelte';
  import { red, green, blue, orange, gray } from 'design/src/tokens/colors';
  import {
    mdiFacebook,
    mdiTwitter,
    mdiDownload,
    mdiLink,
    mdiLoading,
    mdiExportVariant,
  } from '@mdi/js';
  import MaterialIcon from '$lib/MaterialIcon.svelte';
  import SolfegeMemeCanvas from '$lib/memes/SolfegeMemeCanvas.svelte';
  import { uriEncode } from '$lib/uApi';
  import { addAlert } from '$lib/stores/alerts.store';
  import { errorProvider } from '$lib/error-handling/errorProvider';
  import H from '$lib/components/H.svelte';

  let selectedTemplate = solfegeMemeTemplates[0]!;
  let solfegeMemeCanvas: SolfegeMemeCanvas;
  let solfegeString = selectedTemplate.solfege;
  let solfegeFontSize = selectedTemplate.solfegeFontSize;
  let textString = selectedTemplate.text;
  let textFontSize = selectedTemplate.textFontSize;
  let lineSpacingCoefficient = 0.4;
  let dlLink: HTMLAnchorElement;
  const canvasSize = 700;
  let initialLoadComplete = false;
  let linkToShare: string = '/resources/memes/view/11a49786490611';
  let uploading: false | 'twitter' | 'facebook' | 'link' | 'native' = false;

  const backgrounds = [
    { className: 'bg-blue', name: 'blue', hex: blue[500] },
    { className: 'bg-green-600', name: 'green', hex: green[600] },
    { className: 'bg-orange-600', name: 'orange', hex: orange[600] },
    { className: 'bg-red-600', name: 'red', hex: red[600] },
    { className: 'bg-blue-900', name: 'slate', hex: blue[900] },
  ];

  let bgColor = backgrounds[0]!.hex;
  $: activeBackground = backgrounds.find((bg) => bgColor === bg.hex) || backgrounds[0];

  $: {
    loadTemplate(selectedTemplate);
  }

  function loadTemplate(template: SolfegeMemeTemplate) {
    const scale = (n: number) => (n * canvasSize) / 500;
    textString = template.text;
    textFontSize = scale(template.textFontSize);
    solfegeString = template.solfege;
    solfegeFontSize = scale(template.solfegeFontSize);
    lineSpacingCoefficient = template.lineSpacing;
    const bg = backgrounds.find((bg) => bg.name === template.backgroundColor);
    bgColor = bg?.hex || backgrounds[0]!.hex;
  }

  onMount(() => {
    // Load facebook:

    loadTemplate(selectedTemplate);
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('template')) {
        const template = solfegeMemeTemplates.find((t) => t.title === urlParams.get('template'));
        if (template) loadTemplate(template);
      }

      // if (urlParams.get('imageUrl')) {
      // 	imageUrl = urlParams.get('imageUrl')!;
      // }
      if (urlParams.get('textString')) {
        textString = urlParams.get('textString')!;
      }
      if (urlParams.get('solfegeString')) {
        solfegeString = urlParams.get('solfegeString')!;
      }
      // Hex value:
      if (urlParams.has('bgColor')) {
        bgColor =
          backgrounds.find((bg) => bg.hex === urlParams.get('bgColor'))?.hex || backgrounds[0].hex;
      }

      if (urlParams.has('textFontSize') && !Number.isNaN(Number(urlParams.get('textFontSize')))) {
        textFontSize = Number(urlParams.get('textFontSize'));
      }

      if (
        urlParams.has('solfegeFontSize') &&
        !Number.isNaN(Number(urlParams.get('solfegeFontSize')))
      ) {
        solfegeFontSize = Number(urlParams.get('solfegeFontSize'));
      }

      if (
        urlParams.has('lineSpacingCoefficient') &&
        !Number.isNaN(Number(urlParams.get('lineSpacingCoefficient')))
      ) {
        lineSpacingCoefficient = Number(urlParams.get('lineSpacingCoefficient'));
      }
      initialLoadComplete = true;
    }
  });

  $: {
    if (typeof window !== 'undefined' && initialLoadComplete) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('textString', textString);
      urlParams.set('solfegeFontSize', '' + solfegeFontSize);
      urlParams.set('solfegeString', solfegeString);
      urlParams.set('textFontSize', '' + textFontSize);
      urlParams.set('bgColor', activeBackground.hex);
      urlParams.set('lineSpacingCoefficient', '' + lineSpacingCoefficient);
      urlParams.set('template', selectedTemplate.title);
      const newUrl = new URL(`${location.protocol}//${location.host}${location.pathname}`);
      newUrl.search = urlParams.toString();
      window.history.replaceState(
        {},
        textString + ' - Solfege Hand Sign Meme Generator',
        newUrl.toString()
      );
      // Something's changed, invalidate the link!
      linkToShare = '';
      _currentValidShareLink = '';
    }
  }

  function downloadImage() {
    if (!solfegeMemeCanvas) return;
    const dataUrl = solfegeMemeCanvas.getImageDataUrl();
    dlLink.href = dataUrl;
    dlLink.download = 'utheory-meme.png';
    dlLink.target = '_blank';
    dlLink.click();
  }

  let _currentValidShareLink: string;
  let _currentValidShareImageLink: string;
  async function uploadImage(forService: 'facebook' | 'twitter' | 'link' | 'native') {
    if (_currentValidShareLink)
      return { url: _currentValidShareLink, imageUrl: _currentValidShareImageLink };
    uploading = forService;
    let url: string;
    let imageUrl: string;
    try {
      const response = await solfegeMemeCanvas.uploadImageForSharing();
      url = response.url;
      _currentValidShareLink = url;
      imageUrl = response.imageUrl;
      _currentValidShareImageLink = imageUrl;
    } catch (err) {
      uploading = false;
      errorProvider.error(err);
      throw err;
    }
    uploading = false;
    return { url, imageUrl };
  }

  async function facebookShare() {
    linkToShare = '';
    try {
      // FB.ui({
      // 	method: 'share_open_graph'
      // })
      // https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fimgflip.com%2Fi%2F5gxcnc
      const { url } = await uploadImage('facebook');
      FB.ui(
        {
          method: 'share',
          href: url,
          display: 'popup',
        },
        function (response) {
          console.log('response', response);
        }
      );
    } catch (err) {
      errorProvider.error(err);
      addAlert({
        level: 'failure',
        dismissable: true,
        delay: 10,
        message:
          'Sorry, sharing to Facebook failed. Please try again, or download and share the image instead.',
      });
    }
  }

  async function twitterShare() {
    linkToShare = '';
    try {
      // https://twitter.com/intent/tweet
      // ?url=http%3A%2F%2Fcss-tricks.com%2F
      // &text=Tips%2C+Tricks%2C+and+Techniques+on+using+Cascading+Style+Sheets.
      // &hashtags=css,html
      const { url } = await uploadImage('twitter');
      const queryParams = uriEncode({
        url,
        text: textString,
        via: 'uTheorydotcom',
      });
      const tweetLink = `https://twitter.com/intent/tweet${queryParams}`;
      window.open(tweetLink, '_blank');
    } catch (err) {
      errorProvider.error(err);
      addAlert({
        level: 'failure',
        dismissable: true,
        delay: 10,
        message:
          'Sorry, sharing to Twitter failed. Please try again, or download and share the image instead.',
      });
    }
  }

  async function linkShare() {
    linkToShare = '';
    try {
      const { url } = await uploadImage('link');
      linkToShare = url;
      copyShareLink();
    } catch (err) {
      errorProvider.error(err);
      addAlert({
        level: 'failure',
        dismissable: true,
        delay: 10,
        message:
          "Sorry, we weren't able to create a permalink. Please try again, or download and share the image instead.",
      });
    }
  }

  async function nativeShare() {
    if (!navigator.share) return;
    const { url } = await uploadImage('native');
    const file = await solfegeMemeCanvas.getFile();
    const files = Object.freeze([file]);
    // @ts-ignore typescript doesn't yet have navigator.canShare
    if (navigator.share && navigator.canShare && navigator.canShare({ files })) {
      navigator.share({
        title: `${textString}`,
        // @ts-ignore typescript doesn't yet have navigator.canShare
        files,
        url,
        text: `${textString}`,
      });
    } else if (navigator.share) {
      navigator.share({
        title: `${textString}`,
        url,
        text: `${textString}`,
      });
    }
  }

  function copyShareLink() {
    navigator.clipboard.writeText(linkToShare);
    addAlert({
      level: 'success',
      message: 'Link copied to clipboard.',
      dismissable: true,
      delay: 2,
    });
  }
</script>

<svelte:head>
  <script>
    const appId =
      window.location.hostname === 'utheory.com' ? '416189978537727' : '584782565011800';
    window.fbAsyncInit = function () {
      FB.init({
        appId: appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v11.0',
      });
    };
  </script>
  <script
    async
    defer
    crossorigin="anonymous"
    src="https://connect.facebook.net/en_US/sdk.js"></script>
</svelte:head>

<div class="flex flex-wrap justify-between my-16">
  <div class="w-full md:w-1/2 md:pr-8 lg:pr-12 xl:pr-16">
    <PageTitle
      class="mb-4 text-xl"
      title="Solfege Hand Sign Meme Generator"
      titleHeader="{textString} – Solfege Hand Sign Meme Generator"
      description="Kodaly/Curwen Solfege Hand Sign Meme Generator. Create a shareable meme of solfege hand signs for any occasion, including birthdays and holidays."
      seoImageUrl="/teach/solfege-meme-generator/sample-solfege-meme.png"
      seoImageAlt="uTheory Happy Birthday Solfege Hand Sign Meme"
      seoUrl="/teach/resources/memes"
    />

    <div class="mb-4">
      <h2 class="text-lg text-blue">Choose Template:</h2>
      <select bind:value={selectedTemplate} class="border mb-2 p-1 w-full">
        {#each solfegeMemeTemplates as template, i}
          <option value={template}>{template.title}</option>
        {/each}
        solfegeMemeTemplates
      </select>
    </div>

    <div class="mb-4">
      <label
        ><h2 class="text-lg text-blue">Text:</h2>
        <textarea rows="1" class="border mb-2 p-1 w-full" bind:value={textString} />
      </label>
      <br />
      <label for="title-font-size"
        >Text Size:
        <input
          type="range"
          min="5"
          max="300"
          step="1"
          id="title-font-size"
          bind:value={textFontSize}
        />
      </label>
    </div>

    <label for="solfege-input">
      <h2 class="text-lg text-blue">Solfege:</h2>
      <textarea
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        placeholder="Enter solfege, lower case for first-person perspective, upper for third person"
        spellcheck="false"
        cols="30"
        rows="5"
        id="solfege-input"
        class="block border mb-2 p-1 w-full"
        bind:value={solfegeString}
      />
    </label>
    <label for="solfege-font-size">Hand Signs Size:</label>
    <input
      type="range"
      min="5"
      max="300"
      id="solfege-font-size"
      step="1"
      bind:value={solfegeFontSize}
    /><br />
    <label for="line-spacing">Line Spacing:</label>
    <input
      type="range"
      min="0"
      max="1"
      step="0.01"
      id="line-spacing"
      bind:value={lineSpacingCoefficient}
    />
    <div>
      <h2 class="text-lg text-blue mb-1 mt-4">Background Color:</h2>
      {#each backgrounds as bg}
        <label class="{bg.className} text-white px-4 py-2 mx-1/2 inline-block my-2">
          <input type="radio" class="sr-only" bind:group={bgColor} value={bg.hex} />
          <span>{bg.name}</span>
        </label>
        <!-- <button class="{className}">Color</button> -->
      {/each}
    </div>
  </div>

  <div class="w-full md:w-1/2 mx-auto">
    {#if typeof window !== 'undefined'}
      <SolfegeMemeCanvas
        bind:bgColor
        bind:solfegeString
        bind:solfegeFontSize
        bind:textString
        bind:textFontSize
        bind:lineSpacingCoefficient
        bind:this={solfegeMemeCanvas}
        class="w-full mx-auto"
      />
    {:else}
      <H level={3}>{textString}</H>
      <p>{solfegeString}</p>
    {/if}

    <div class="mt-4 text-center">
      {#if typeof navigator !== 'undefined' && navigator.share}
        <button on:click={nativeShare}
          ><MaterialIcon
            size="52px"
            color={gray[500]}
            label="Native share"
            spin={uploading === 'native'}
            path={uploading !== 'native' ? mdiExportVariant : mdiLoading}
          />
        </button>
      {/if}
      <button on:click={facebookShare}
        ><MaterialIcon
          size="52px"
          color="#4267B2"
          label="Facebook share"
          spin={uploading === 'facebook'}
          path={uploading !== 'facebook' ? mdiFacebook : mdiLoading}
        />
      </button>

      <button on:click={twitterShare}
        ><MaterialIcon
          size="52px"
          color="#1DA1F2"
          label="Twitter share"
          spin={uploading === 'twitter'}
          path={uploading !== 'twitter' ? mdiTwitter : mdiLoading}
        />
      </button>
      <button on:click={linkShare}
        ><MaterialIcon
          size="52px"
          color="black"
          label="Get permalink"
          spin={uploading === 'link'}
          path={uploading !== 'link' ? mdiLink : mdiLoading}
        />
      </button>

      <button on:click={downloadImage}
        ><MaterialIcon size="52px" color="var(--blue-500)" label="Download" path={mdiDownload} />
      </button>
      <!-- <button class="border p-4 m-2" on:click={uploadImageForSharing}>Upload Image</button> -->
      {#if linkToShare}
        <div class="border my-2 p-2 shadow-lg text-left flex">
          <input
            type="text"
            class="bg-gray-50 flex-grow border border-blue text-sm"
            value={linkToShare}
            on:focus={function () {
              // @ts-expect-error - until ts syntax is allowed in templates.
              this.select();
              copyShareLink();
            }}
            on:input={function () {
              // @ts-expect-error - until ts syntax is allowed in templates.
              this.value = linkToShare;
            }}
          />
          <button class="bg-cyan px-2 py-1 ml-2 text-white" on:click={copyShareLink}>COPY</button>
        </div>
      {/if}
    </div>
  </div>
</div>

<p class="text-gray mt-8">
  These memes are made using the free <a href="./kodaly-curwen-solfege-hand-signs-font" class="a"
    >uTheory Kodaly/Curwen Hand Sign Font & Images</a
  >.
</p>
<div class="text-gray">
  <p class="my-4">If you like this, you might enjoy these related resources:</p>
  <ul class=" list-disc ml-8">
    <li>
      <a
        class="a"
        rel="external"
        href="https://info.utheory.com/8-ways-to-use-utheorys-meme-generator-with-your-students/"
        >Eight Ways to Use uTheory's Solfege Meme Generator</a
      > in your classroom
    </li>
    <li>
      uTheory's <a class="a" href="/app/lessons">Ear Training Lessons</a>
    </li>
    <li>
      uTheory's <a class="a" href="/app/practice">Ear Training Practice</a>
    </li>
    <li><a class="a" href="/teach/resources">Printable Music Theory Resources</a> from uTheory</li>
  </ul>
</div>

<a class="hidden" bind:this={dlLink} />

<!-- 
Template:<br />
<div class="bg-gray-900">
	<pre>
  <code class="font-mono bg-gray-900 text-white ml-4">
    &lcub;
      title: '',
      text: `{textString}`,
      textFontSize: {textFontSize},
      solfege: `{solfegeString}`,
      solfegeFontSize: {solfegeFontSize},
			lineSpacing: {lineSpacingCoefficient},
    &rcub;
  </code>
</pre>
</div> -->
<style lang="postcss">
  @font-face {
    font-family: 'utheory-solfege';
    src: url('/teach/solfege-meme-generator/utheory-solfege-0.1.3.woff') format('woff'),
      /* Pretty Modern Browsers */ url('/teach/solfege-meme-generator/utheory-solfege-0.1.3.ttf')
        format('truetype'),
      /* Safari, Android, iOS */ url('/teach/solfege-meme-generator/utheory-solfege-0.1.3.svg')
        format('svg'); /* Legacy iOS */
  }

  .title {
    font-family: serif;
  }
  .solfege {
    font-family: 'utheory-solfege', sans-serif;
    line-height: 150%;
    word-wrap: break-word;
  }

  input:checked + span {
    @apply font-bold;
  }
</style>
